/*
 * <store-locator-map> custom element.
 *
 * Sticky right column of the desktop store-locator results layout. Boots the
 * Google Maps JS API + a clustered marker layer (one AdvancedMarkerElement per
 * store card), and listens for `store-locator:state-changed` events from the
 * parent <store-locator>.
 *
 * VIS-601: on load (unlocked, no search) the map frames the whole country and
 * shows the entire network clustered; the cards list follows the viewport. A
 * search / region / radius (or a locked locator) instead frames that result.
 * VIS-301: the cards list follows the map viewport (capped at the nearest N).
 * For performance the clusterer only holds markers within the current viewport
 * (plus a margin); off-screen markers are removed so we never render hundreds of
 * pins for stores you can't see — and they reveal again on zoom-out.
 *
 * Gates on (a) viewport ≥ DESKTOP_MIN_PX, (b) a non-empty API key on the host.
 * Below the breakpoint the element no-ops so the ~200 KB Maps bundle is never
 * fetched on mobile.
 *
 * Cross-linkage:
 *   - Marker click → scroll the matching card into view + brief highlight.
 *   - Card hover/focus → raise the matching marker's z-index + swap icon variant.
 *
 * fitBounds runs on every state change except `search-keystroke` (would jitter
 * on each typed character) and while the visitor is browsing the map themselves
 * (VIS-301 viewport-follow: re-fitting would fight their pan/zoom). When the map
 * settles, it reports its bounds to <store-locator> so the cards list re-filters
 * to the stores in view.
 */

import { MarkerClusterer, SuperClusterAlgorithm } from '@theme/markerclusterer';
import { loadMapsApi } from '@theme/store-locator-maps-loader';

const DESKTOP_MIN_PX = 1024;
const MAX_FIT_ZOOM = 14;
// VIS-301: SuperCluster grouping radius in px. Larger = more aggressive
// clustering = fewer rendered markers when zoomed out (perf). See #initMap.
const CLUSTER_RADIUS_PX = 200;
// VIS-301: coalesce the viewport→list re-filter across a zoom/pan burst so the
// card list re-renders once on settle rather than on every intermediate idle.
const VIEWPORT_SYNC_DEBOUNCE_MS = 150;
// VIS-301: only stores within the viewport (expanded by this fraction on each
// side) are kept in the clusterer — off-screen markers are removed so they don't
// render. The margin pre-loads a ring just off screen for smooth panning.
const VIEWPORT_MARKER_MARGIN = 0.5;
// VIS-301: zoom used when a search/user point exists but no store falls within
// the radius — frames the area so nearby clusters stay visible (the map becomes
// the "explore wider" affordance) rather than snapping back to all of Italy.
const EMPTY_RADIUS_ZOOM = 9;
const ITALY_CENTER = { lat: 42.5, lng: 12.5 };
const HIGHLIGHT_MS = 1500;

class StoreLocatorMap extends HTMLElement {
  connectedCallback() {
    // Desktop boots eagerly. On mobile the map lives behind the "Mappa" view
    // tab, so we defer setup until <store-locator> calls activate() on the
    // first switch — that's what keeps the ~200 KB Maps bundle off mobile
    // page loads where the visitor never opens the map.
    if (window.matchMedia(`(min-width: ${DESKTOP_MIN_PX}px)`).matches) {
      this.activate();
    }
  }

  // Idempotent. Called on desktop connect, or by the parent <store-locator>
  // when the mobile "Mappa" tab is opened for the first time.
  activate() {
    if (this.activated) return;
    this.activated = true;
    this.#setup();
  }

  #setup() {
    this.locator = this.closest('store-locator');
    if (!this.locator) return;

    this.apiKey = this.dataset.apiKey || '';
    if (!this.apiKey) {
      console.warn(
        '[store-locator-map] No Google Maps API key configured; map column will stay blank. ' +
          'Set it in Shopify Admin → Settings → Custom data → Shop → "Google Maps API key".'
      );
      return;
    }

    this.mapId = this.dataset.mapId || '';
    this.defaultZoom = parseInt(this.dataset.defaultZoom, 10) || 6;
    this.markerAriaTemplate = this.dataset.labelMarkerAria || '';
    this.clusterAriaTemplate = this.dataset.labelClusterAria || '';
    this.userLocationAria = this.dataset.labelUserLocationAria || '';
    this.youAreHereLabel = this.dataset.labelYouAreHere || '';

    this.mapEl = this.querySelector('[data-map]');

    this.markersByHandle = new Map();
    this.markersInClusterer = new Map(); // handle → boolean
    this.highlightTimers = new Map();
    // VIS-601: true once the visitor has expressed location intent (search /
    // region / radius) or the locator is locked — drives "fit to result" vs the
    // default country-wide view.
    this.hasIntent = false;
    this.ready = false;
    // VIS-301: muted while we move the camera ourselves so our own fits/recenters
    // don't get mistaken for the visitor panning/zooming. See #beginProgrammaticMove.
    this.programmaticMove = false;

    this.locator.addEventListener('store-locator:state-changed', this.#handleStateChanged);
    this.locator.addEventListener('mouseover', this.#handleCardHover);
    this.locator.addEventListener('mouseout', this.#handleCardHover);
    this.locator.addEventListener('focusin', this.#handleCardHover);
    this.locator.addEventListener('focusout', this.#handleCardHover);

    void this.#boot();
  }

  disconnectedCallback() {
    if (this.locator) {
      this.locator.removeEventListener('store-locator:state-changed', this.#handleStateChanged);
      this.locator.removeEventListener('mouseover', this.#handleCardHover);
      this.locator.removeEventListener('mouseout', this.#handleCardHover);
      this.locator.removeEventListener('focusin', this.#handleCardHover);
      this.locator.removeEventListener('focusout', this.#handleCardHover);
    }
    this.highlightTimers.forEach((t) => clearTimeout(t));
    this.highlightTimers.clear();
    if (this.programmaticMoveTimer) clearTimeout(this.programmaticMoveTimer);
    if (this.viewportSyncTimer) clearTimeout(this.viewportSyncTimer);
    if (this.idleListener) {
      this.idleListener.remove();
      this.idleListener = null;
    }
    if (this.clusterer) {
      this.clusterer.setMap(null);
      this.clusterer = null;
    }
    this.markersByHandle.clear();
  }

  // -- Boot -----------------------------------------------------------------

  async #boot() {
    try {
      await loadMapsApi(this.apiKey);
    } catch (err) {
      console.warn('[store-locator-map] Failed to load Google Maps JS API', err);
      return;
    }
    await this.#initMap();
    this.ready = true;
    // Sync to the parent's current state. VIS-601: the default state frames the
    // country and clusters every store, but only those within the current
    // viewport (#reconcileMarkersToBounds) are ever added to the clusterer, so
    // the rendered marker count stays proportional to what's on screen rather
    // than the full ~477. The set fills in as the parent hydrates more pages.
    this.#applyStateToMap(this.locator ? this.locator.filterState : null, 'init');
  }

  async #initMap() {
    const { Map: GMap } = await google.maps.importLibrary('maps');
    await google.maps.importLibrary('marker');
    const opts = {
      center: ITALY_CENTER,
      zoom: this.defaultZoom,
      disableDefaultUI: false,
      clickableIcons: false,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
    };
    if (this.mapId) opts.mapId = this.mapId;
    this.mapInstance = new GMap(this.mapEl, opts);

    this.clusterer = new MarkerClusterer({
      map: this.mapInstance,
      markers: [],
      // VIS-301: cluster radius bumped 60 → 200 px so the in-view store set
      // collapses into far fewer bubbles when zoomed out — keeping the count of
      // rendered AdvancedMarkerElements (the DOM cost) low instead of leaving
      // many unclustered pins, which made zoom-out feel sluggish. Works together
      // with #reconcileMarkersToBounds (off-screen markers aren't managed at all).
      // At street zoom stores are still pixels apart so close-up granularity
      // holds; maxZoom 14 still fully declusters in tight.
      algorithm: new SuperClusterAlgorithm({ radius: CLUSTER_RADIUS_PX, maxZoom: 14 }),
      // VIS-300: brand-styled cluster bubbles in place of the library's stock
      // blue/red DefaultRenderer. onClusterClick is left at its default
      // (zoom-to-bounds) by not passing it.
      renderer: { render: (cluster, stats) => this.#buildClusterContent(cluster, stats) },
    });

    // VIS-301: a user-driven pan/zoom re-filters the cards list to the visible
    // map area (see <store-locator>.syncListToViewport). The clusterer adds its
    // own idle listener for re-clustering; this one is for list sync. Our own
    // programmatic camera moves are gated out via #beginProgrammaticMove so they
    // don't masquerade as visitor intent.
    this.idleListener = this.mapInstance.addListener('idle', () => this.#handleMapIdle());
  }

  // -- Viewport → list sync -------------------------------------------------

  // Mute the viewport→list sync while we move the camera ourselves (initial fit,
  // recenters, the MAX_FIT_ZOOM clamp). fitBounds animates and can emit several
  // idles; a short window covers them all, then visitor pans/zooms re-enable
  // sync. Browser timers are fine here (this is not the headless workflow sandbox).
  #beginProgrammaticMove() {
    this.programmaticMove = true;
    if (this.programmaticMoveTimer) clearTimeout(this.programmaticMoveTimer);
    this.programmaticMoveTimer = setTimeout(() => {
      this.programmaticMove = false;
    }, 1200);
  }

  #handleMapIdle() {
    // VIS-601: the map is always active now (default country view included), so
    // reconcile on every settle — guard only on a real, sized map (a hidden
    // mobile Lista tab reports bogus world bounds).
    if (!this.locator || !this.mapInstance || !this.#isVisible()) return;
    // Reconcile the rendered marker set to the viewport on *every* settle,
    // including our own fits — that's what keeps off-screen pins from painting.
    this.#reconcileMarkersToBounds();
    // The cards-list sync, by contrast, only follows *user* moves (muted during
    // our programmatic fits to avoid a feedback loop) and is debounced: a
    // zoom/pan burst fires several idles, but the list should re-render once on
    // settle, off the map's render frame.
    if (this.programmaticMove) return;
    if (typeof this.locator.syncListToViewport !== 'function') return;
    if (this.viewportSyncTimer) clearTimeout(this.viewportSyncTimer);
    this.viewportSyncTimer = setTimeout(() => this.#emitViewport(), VIEWPORT_SYNC_DEBOUNCE_MS);
  }

  #emitViewport() {
    if (this.programmaticMove || !this.mapInstance || !this.#isVisible()) return;
    if (!this.locator || typeof this.locator.syncListToViewport !== 'function') return;
    const bounds = this.mapInstance.getBounds();
    if (!bounds) return;
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    this.locator.syncListToViewport({
      north: ne.lat(),
      south: sw.lat(),
      east: ne.lng(),
      west: sw.lng(),
    });
  }

  // -- Cluster marker -------------------------------------------------------

  // VIS-300: custom MarkerClusterer renderer. Mirrors #buildMarkerContent — a
  // DOM wrapper styled via .store-locator__cluster (burgundy circle + white
  // count), sized in tiers by the contained store count. `stats` is unused
  // (fixed count thresholds rather than mean-based tiering).
  #buildClusterContent({ count, position }) {
    const tier = count < 10 ? 'sm' : count < 50 ? 'md' : 'lg';
    const wrapper = document.createElement('div');
    wrapper.className = `store-locator__cluster store-locator__cluster--${tier}`;
    wrapper.textContent = String(count);

    return new google.maps.marker.AdvancedMarkerElement({
      position,
      content: wrapper,
      // Keep the cluster clickable so the default onClusterClick zoom fires.
      gmpClickable: true,
      // Mirror the library's z-index stacking (denser clusters sit on top).
      zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
      title: this.clusterAriaTemplate.replace('{{ count }}', count).replace('{count}', count),
    });
  }

  // -- Marker layer ---------------------------------------------------------

  // Create the marker for a card but do NOT add it to the clusterer or set
  // its `map`. The clusterer is the source of truth for visibility — caller
  // decides whether to push it in.
  #ensureMarker(card) {
    const handle = card.dataset.handle;
    if (!handle || this.markersByHandle.has(handle)) return null;
    const lat = parseFloat(card.dataset.lat);
    const lng = parseFloat(card.dataset.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    const name = card.querySelector('.store-card__name')?.textContent?.trim() || '';
    const content = this.#buildMarkerContent(handle);

    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: { lat, lng },
      content,
      title: this.markerAriaTemplate.replace('{{ name }}', name).replace('{name}', name),
      gmpClickable: true,
    });
    marker.addEventListener('gmp-click', () => this.#scrollCardIntoView(handle));

    this.markersByHandle.set(handle, marker);
    return marker;
  }

  #buildMarkerContent(handle) {
    const wrapper = document.createElement('div');
    wrapper.className = 'store-locator__marker';
    wrapper.dataset.markerHandle = handle;
    wrapper.innerHTML =
      '<svg width="28" height="36" viewBox="0 0 28 36" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M14 0C6.27 0 0 6.27 0 14c0 9.94 12.41 21.27 12.94 21.74a1.6 1.6 0 0 0 2.12 0C15.59 35.27 28 23.94 28 14 28 6.27 21.73 0 14 0Z" fill="currentColor"/>' +
      '<circle cx="14" cy="14" r="5" fill="#fff"/>' +
      '</svg>';
    return wrapper;
  }

  // VIS-301: keep the clusterer's marker set bounded to the visible map area
  // (plus a margin) instead of holding all ~477 at once. Google Maps renders a
  // DOM node for every managed AdvancedMarkerElement even when it's far off
  // screen, so holding the full set left hundreds of off-viewport pins painting
  // at mid zoom — the source of the zoom-out lag. Reconciling to the viewport on
  // each settle keeps the rendered count proportional to what's on screen.
  //
  // Reveal-on-zoom still works for free: zooming out grows the viewport, so the
  // newly-in-view stores get added and cluster; zooming in drops the far ones.
  // The margin pre-loads a ring just off screen so a small pan stays smooth.
  // `markersInClusterer` is the membership guard so we only add/remove deltas.
  #reconcileMarkersToBounds() {
    if (!this.locator || !this.clusterer || !this.mapInstance) return;
    const b = this.mapInstance.getBounds();
    if (!b) return;
    const ne = b.getNorthEast();
    const sw = b.getSouthWest();
    const latPad = (ne.lat() - sw.lat()) * VIEWPORT_MARKER_MARGIN;
    const lngPad = (ne.lng() - sw.lng()) * VIEWPORT_MARKER_MARGIN;
    const north = ne.lat() + latPad;
    const south = sw.lat() - latPad;
    const east = ne.lng() + lngPad;
    const west = sw.lng() - lngPad;

    const toAdd = [];
    const toRemove = [];
    this.locator.querySelectorAll('[data-store-card]').forEach((card) => {
      const handle = card.dataset.handle;
      if (!handle) return;
      const lat = parseFloat(card.dataset.lat);
      const lng = parseFloat(card.dataset.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const inBounds = lat >= south && lat <= north && lng >= west && lng <= east;
      const inClusterer = this.markersInClusterer.get(handle) === true;
      if (inBounds && !inClusterer) {
        const marker = this.markersByHandle.get(handle) || this.#ensureMarker(card);
        if (!marker) return;
        toAdd.push(marker);
        this.markersInClusterer.set(handle, true);
      } else if (!inBounds && inClusterer) {
        const marker = this.markersByHandle.get(handle);
        if (marker) toRemove.push(marker);
        this.markersInClusterer.set(handle, false);
      }
    });

    if (toRemove.length) this.clusterer.removeMarkers(toRemove, true);
    if (toAdd.length) this.clusterer.addMarkers(toAdd, true);
    if (toAdd.length || toRemove.length) this.clusterer.render();
  }

  // -- Default vs. located ---------------------------------------------------
  // VIS-601: the map is always active. With no location intent it shows the
  // whole network clustered country-wide (#centerDefault + reconcile); once the
  // visitor searches / picks a region / runs a radius search — or the locator is
  // locked to a subset — it frames that result instead (#fitToVisible).

  #hasLocationIntent(filterState) {
    // A locked locator (campaign / region / service pre-filter) is a small fixed
    // subset — frame it like a result rather than the whole country.
    if (this.locator?.locked) return true;
    if (!filterState) return false;
    // VIS-1087: a real default anchor (the visitor's granted position or the
    // customer's address) frames its nearest stores; only the Italy centroid
    // keeps the country-wide view. Erasing a search nulls lat/lng but leaves
    // anchorSource behind, hence the coordinate check.
    const realAnchor =
      (filterState.anchorSource === 'geolocate' || filterState.anchorSource === 'customer') &&
      Number.isFinite(filterState.lat);
    return (
      !!filterState.q ||
      !!filterState.region ||
      filterState.mode === 'radius' ||
      realAnchor
    );
  }

  #centerDefault() {
    if (!this.mapInstance) return;
    this.#beginProgrammaticMove();
    this.mapInstance.setCenter(ITALY_CENTER);
    this.mapInstance.setZoom(this.defaultZoom);
  }

  // -- State-change handler -------------------------------------------------

  #handleStateChanged = (event) => {
    if (!this.ready) return;
    const { reason, filterState } = event.detail || {};
    this.#applyStateToMap(filterState, reason);
  };

  #applyStateToMap(filterState, reason) {
    const hadIntent = this.hasIntent === true;
    const hasIntent = this.#hasLocationIntent(filterState);
    this.hasIntent = hasIntent;

    this.#syncUserLocationMarker(filterState);

    if (!hasIntent) {
      // VIS-601 default state: frame the whole country and let the idle reconcile
      // cluster every in-view store. Recenter only when entering the default
      // state (initial load, or clearing a search) — on a 'hydrate'/'viewport'
      // pass there's no camera move, so reconcile directly to pick up the
      // newly-appended (or newly-relevant) markers.
      if (reason === 'init' || hadIntent) {
        this.#centerDefault();
      } else {
        this.#reconcileMarkersToBounds();
      }
      return;
    }

    // Located / locked: frame the result subset. Skip fitBounds on every-
    // keystroke updates *unless* this is the transition into a search — then the
    // map should focus the result. VIS-301: also skip while the visitor browses
    // the map (viewport-follow) — re-fitting on the resulting 'viewport'/
    // 'hydrate' updates would fight their pan/zoom.
    const justBecameIntent = !hadIntent;
    const viewportActive = !!(filterState && filterState.viewport);
    if (!viewportActive && (reason !== 'search-keystroke' || justBecameIntent)) {
      // Fit reframes the map; the resulting idle reconciles the marker set to
      // the new viewport (#reconcileMarkersToBounds), so we don't add here.
      this.#fitToVisible(filterState);
    } else {
      // No reframe (viewport-follow update, a non-fit keystroke, or the hydrate
      // pass that appends ~425 cards). There's no camera move to trigger a
      // reconcile, so do it now — otherwise newly-relevant markers (e.g. just-
      // hydrated stores inside the view) wouldn't appear until the next move.
      this.#reconcileMarkersToBounds();
    }
  }

  // -- User-location marker -------------------------------------------------

  #syncUserLocationMarker(filterState) {
    if (!this.mapInstance) return;
    const isUser = filterState && filterState.userLocation === 'geolocate';
    const hasCoords =
      filterState && Number.isFinite(filterState.lat) && Number.isFinite(filterState.lng);

    if (!isUser || !hasCoords) {
      if (this.userLocationMarker) {
        this.userLocationMarker.map = null;
      }
      return;
    }

    if (!this.userLocationMarker) {
      const wrapper = document.createElement('div');
      wrapper.className = 'store-locator__marker store-locator__marker--user';
      wrapper.dataset.markerUser = 'true';
      wrapper.innerHTML =
        '<svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<circle cx="10" cy="10" r="9" fill="rgba(0,102,255,0.18)"/>' +
        '<circle cx="10" cy="10" r="5" fill="#06f" stroke="#fff" stroke-width="2"/>' +
        '</svg>';
      this.userLocationMarker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: filterState.lat, lng: filterState.lng },
        content: wrapper,
        title: this.userLocationAria || this.youAreHereLabel,
        zIndex: 9999,
      });
    } else {
      this.userLocationMarker.position = { lat: filterState.lat, lng: filterState.lng };
    }
    this.userLocationMarker.map = this.mapInstance;
  }

  // -- Bounds ---------------------------------------------------------------

  // VIS-283: the mobile Lista negozi tab leaves the map at display:none.
  // fitBounds on a 0-sized container resolves to "the whole world" and the
  // bad zoom sticks around when the visitor switches back. Treat the map as
  // hidden whenever offsetParent is null (or its box collapses) and defer.
  #isVisible() {
    return this.offsetParent !== null && this.offsetWidth > 0 && this.offsetHeight > 0;
  }

  // Public: called by the parent <store-locator> when the Mappa tab becomes
  // visible. Tells Google Maps to relayout to its new size, then replays any
  // fit that was deferred while the map was hidden.
  onBecameVisible() {
    if (!this.ready || !this.mapInstance) return;
    google.maps.event.trigger(this.mapInstance, 'resize');
    if (this.pendingFit) {
      const { filterState } = this.pendingFit;
      this.pendingFit = null;
      this.#fitToVisible(filterState);
    }
  }

  // Public: re-frame the map to the default whole-country view. The parent calls
  // this when the visitor clears the "in this map area" chip (VIS-601) so the
  // map and the list realign on the full network rather than the panned area.
  recenterDefault() {
    if (!this.ready || !this.mapInstance || this.hasIntent) return;
    this.#centerDefault();
  }

  #fitToVisible(filterState) {
    if (!this.mapInstance) return;
    if (!this.#isVisible()) {
      this.pendingFit = { filterState };
      return;
    }
    this.pendingFit = null;
    // Everything below moves the camera; mute the viewport→list sync for the
    // window so the resulting idle(s) don't re-enter as a "user pan/zoom".
    this.#beginProgrammaticMove();
    const includeUserPoint = !!filterState && filterState.userLocation === 'geolocate';

    // VIS-301: frame the *result*, not the whole network. Build bounds from the
    // visible (radius-filtered) cards' markers + the user-location point. Ensure
    // each visible card's marker exists (it may not be in the clusterer yet —
    // membership is reconciled to the viewport on the resulting idle).
    const bounds = new google.maps.LatLngBounds();
    let any = false;
    // VIS-1000: a favorite pinned from outside the results stays out of the
    // framing, or a far-away favorite would zoom the map out.
    this.locator.querySelectorAll('[data-store-card]:not([hidden]):not([data-store-pinned])').forEach((card) => {
      const marker = this.markersByHandle.get(card.dataset.handle) || this.#ensureMarker(card);
      if (!marker) return;
      bounds.extend(marker.position);
      any = true;
    });
    if (includeUserPoint && this.userLocationMarker && this.userLocationMarker.map) {
      bounds.extend(this.userLocationMarker.position);
      any = true;
    }

    if (!any) {
      // Empty radius: no store within N km. If we still have a search/user
      // point, center on it at a moderate zoom so the nearest clusters are
      // visible — only fall back to the whole-country view when there's no
      // point at all (shouldn't happen while narrowing).
      const hasPoint =
        !!filterState && Number.isFinite(filterState.lat) && Number.isFinite(filterState.lng);
      if (hasPoint) {
        this.mapInstance.setCenter({ lat: filterState.lat, lng: filterState.lng });
        this.mapInstance.setZoom(EMPTY_RADIUS_ZOOM);
      } else {
        this.mapInstance.setCenter(ITALY_CENTER);
        this.mapInstance.setZoom(this.defaultZoom);
      }
      return;
    }

    this.mapInstance.fitBounds(bounds, 48);
    google.maps.event.addListenerOnce(this.mapInstance, 'idle', () => {
      if (this.mapInstance.getZoom() > MAX_FIT_ZOOM) {
        // Re-arm the mute window: this clamp is another programmatic move whose
        // idle must not be read as the visitor zooming.
        this.#beginProgrammaticMove();
        this.mapInstance.setZoom(MAX_FIT_ZOOM);
      }
    });
  }

  // -- Cross-linkage --------------------------------------------------------

  #scrollCardIntoView(handle) {
    const card = this.#findCard(handle);
    if (!card) return;
    // VIS-283: on mobile the map and the list don't share the screen — when
    // the Mappa tab is active the list is display:none, so the scroll target
    // doesn't exist on screen. Flip back to the Lista negozi tab first, then
    // wait one frame for the layout swap before scrolling and highlighting.
    if (this.locator && this.locator.dataset.viewMode === 'map') {
      this.locator.setViewMode('list');
      window.requestAnimationFrame(() => this.#highlightCard(card, handle));
      return;
    }
    this.#highlightCard(card, handle);
  }

  #highlightCard(card, handle) {
    card.scrollIntoView({ behavior: 'smooth', block: 'start' });
    card.dataset.highlight = 'true';
    const prev = this.highlightTimers.get(handle);
    if (prev) clearTimeout(prev);
    this.highlightTimers.set(
      handle,
      setTimeout(() => {
        delete card.dataset.highlight;
        this.highlightTimers.delete(handle);
      }, HIGHLIGHT_MS)
    );
  }

  #handleCardHover = (event) => {
    const card = event.target.closest && event.target.closest('[data-store-card]');
    if (!card) return;
    const handle = card.dataset.handle;
    if (!handle) return;
    const marker = this.markersByHandle.get(handle);
    if (!marker) return;
    const isOn = event.type === 'mouseover' || event.type === 'focusin';
    const content = marker.content;
    if (content && content.classList) {
      content.classList.toggle('is-highlighted', isOn);
    }
    marker.zIndex = isOn ? 1000 : null;
  };

  #findCard(handle) {
    if (!this.locator) return null;
    return this.locator.querySelector(
      `[data-store-card][data-handle="${cssEscape(handle)}"]`
    );
  }
}

// -- Helpers ---------------------------------------------------------------

function cssEscape(value) {
  if (window.CSS && typeof window.CSS.escape === 'function') {
    return window.CSS.escape(value);
  }
  return String(value).replace(/["\\]/g, '\\$&');
}

if (!customElements.get('store-locator-map')) {
  customElements.define('store-locator-map', StoreLocatorMap);
}
